import {
  useEstimatePaymentFee,
  useDepositState,
  useDepositActions,
  useDepositValidation,
  useAlfredSupportedOptions,
  useAlfredQuotePreview,
  useCalculatedLimits,
} from "src/hooks";
import { PaymentType } from "src/api";
import { useUrlParamsContext, useDepositWithdrawContext } from "src/contexts";

export const useDeposit = () => {
  // Get context data
  const { balance } = useDepositWithdrawContext();
  const { paymentRouteId } = useUrlParamsContext();
  const { currency } = balance;

  // State management
  const state = useDepositState();
  const {
    amount,
    amountTouched,
    setAmountTouched,
    initiateDepositLoading,
    initiateDepositError,
    bankId,
    setBankId,
  } = state;

  // Currency picker: list of supported fiats + the user's current pick
  const {
    options,
    selected: selectedOption,
    selectedCurrency,
    setSelectedCurrency,
    loading: optionsLoading,
  } = useAlfredSupportedOptions({ type: "deposit" });

  // Only quote amounts within the route's limits — Alfred rejects an
  // out-of-range amount with a 409, which surfaced as a broken preview.
  // Gate the quoted amount on [min,max]; the limit error is shown by
  // useDepositValidation instead.
  const numericAmount = Number(amount);
  const minAmount = Number(selectedOption?.min_amount);
  const maxAmount = Number(selectedOption?.max_amount);
  const amountInRange =
    !!amount &&
    Number.isFinite(numericAmount) &&
    (!Number.isFinite(minAmount) || numericAmount >= minAmount) &&
    (!Number.isFinite(maxAmount) || numericAmount <= maxAmount);

  // Clamp the typed amount into the route's [min, max] when the field
  // loses focus, so the input can't keep an out-of-range value.
  const handleAmountBlur = () => {
    setAmountTouched(true);
    if (!amount || !Number.isFinite(numericAmount)) return;
    let clamped = numericAmount;
    if (Number.isFinite(maxAmount) && clamped > maxAmount) clamped = maxAmount;
    if (Number.isFinite(minAmount) && clamped < minAmount) clamped = minAmount;
    if (clamped !== numericAmount) state.setAmount(String(clamped));
  };

  // Live preview from Alfred — `toAmount` is the stable-equivalent we
  // use for platform-side validation/fee. Stays null until the user
  // types a positive in-range amount and the debounced fetch resolves.
  const { quote, loading: quoteLoading, secondsUntilExpiry } = useAlfredQuotePreview({
    sourceCurrency: selectedOption?.source_currency ?? null,
    targetCurrency: selectedOption?.target_currency ?? null,
    amount: amountInRange ? amount : "",
  });
  const stableAmount = quote ? Number(quote.toAmount) : 0;

  // Platform-side fee runs against the stable-equivalent (USDC/USDT),
  // not the raw fiat input — fee tiers are configured per route in
  // route.currency_id which is always the stable side.
  const { paymentFee, paymentFeeLoading, paymentFeeError } = useEstimatePaymentFee({
    paymentType: PaymentType.Deposit,
    amount: stableAmount,
  });

  const {
    limitsStatus,
    availableLimit,
    loading: limitsLoading,
    refetch: refetchLimits,
  } = useCalculatedLimits(PaymentType.Deposit);

  // Validation: Alfred limits run against fiat input (selectedOption);
  // platform limits / fee-after-amount run against stableAmount.
  const { errors } = useDepositValidation({
    amount,
    stableAmount,
    paymentFeeError,
    paymentFee,
    selectedOption,
    availableLimit,
  });

  // Actions
  const actions = useDepositActions({
    amount,
    setAmount: state.setAmount,
    setIsLoading: state.setInitiateDepositLoading,
    setError: state.setInitiateDepositError,
    setSessionData: state.setSessionData,
    setPaymentInstructions: state.setPaymentInstructions,
    currencyPrecision: currency.precision,
    paymentRouteId,
    sourceCurrencyId: selectedCurrency,
    bankId,
  });

  // PSE-only gate: Alfred 422s onramp with `metadata.bankId required`
  // on the PSE rail. ACH (other Colombia rail) doesn't need it, so the
  // gate has to check the rail and not just the COP currency.
  const requiresBankId =
    (selectedOption?.source_currency ?? "").toUpperCase() === "COP" &&
    (selectedOption?.rail ?? "").toUpperCase() === "PSE";
  const bankIdValid = !requiresBankId || /^\d{4}$/.test(bankId);

  // Compute submit disabled state. Quote must be loaded (we need a
  // valid stable-amount for platform validation) and amount must
  // satisfy both Alfred and platform limits.
  const submitDisabled =
    paymentFeeLoading ||
    quoteLoading ||
    initiateDepositLoading ||
    !!initiateDepositError ||
    !selectedCurrency ||
    !quote ||
    !bankIdValid ||
    Object.values(errors).some((error) => error.length > 0) ||
    !amount;

  return {
    // Session id (for polling transaction status + dev emit endpoint)
    sessionId: state.sessionData?.session_id ?? null,

    // Reset to put the form back when the user closes the expiry
    // modal or hits "start over" after a failed deposit.
    resetDeposit: () => {
      state.setPaymentInstructions(null);
      state.setSessionData(null as never);
      state.setInitiateDepositError(null);
      // Returning to the form after a deposit — refresh used limit totals.
      refetchLimits();
    },

    // Inline rails — bank instructions to render after Next click
    paymentInstructions: state.paymentInstructions,

    // Method-not-available signal — backend returns empty options when
    // the user's KYC country isn't on Alfred's allowlist.
    notAvailableForCountry: !optionsLoading && options.length === 0,

    // Amount (fiat input)
    amount,
    amountTouched,
    setAmountTouched,

    // Live quote (Alfred)
    quote,
    quoteLoading,
    stableAmount,
    // Countdown until Alfred's quote expires + auto-refresh on hit zero
    secondsUntilExpiry,

    // Platform fee (in stable currency)
    paymentFee,
    paymentFeeLoading,

    // Platform limit group (used/limit/remaining, in wallet currency)
    limitsStatus,
    availableLimit,
    limitsLoading,

    // Currency (wallet target — what they'll receive)
    currency,

    // Source-currency picker (what they'll pay)
    options,
    selectedOption,
    selectedCurrency,
    setSelectedCurrency,
    optionsLoading,

    // Validation
    errors,
    submitDisabled,

    // Loading and error
    initiateDepositLoading,
    initiateDepositError,

    // Actions
    handleAmountChange: actions.handleAmountChange,
    handleAmountBlur,
    handleInitiateDeposit: actions.handleInitiateDeposit,

    // CO-only bank-id input (free text). Form renders only when
    // `requiresBankId` is true; `bankIdValid` mirrors the BE DTO
    // shape (`/^\d{4}$/`) and gates submit.
    bankId,
    setBankId,
    requiresBankId,
    bankIdValid,
  };
};
